import React from 'react';
import { useParams } from 'react-router-dom';
import { Box, Divider, Stack, Typography } from '@mui/material';
import DnsIcon from '@mui/icons-material/Dns';
import { CustomDomainSection } from '@modules/custom-domains';
import {
  ARTICLE_SITE_DOMAIN_SECTION_TEST_ID,
  ARTICLE_SITE_PAGE_TEST_ID,
} from '@modules/support-flow/constants/articleSiteTestIds';

/**
 * Articles → "Site & domain" (S-4): where the article-surface domain is connected.
 * Layout only; the domain wizard/state machine is owned by `CustomDomainSection`.
 * The "Path on your main site" section lands inside this page with the path-mode panel.
 */
export function ArticleSitePage(): React.ReactElement | null {
  const { projectId } = useParams<{ projectId: string }>();
  if (!projectId) return null;

  return (
    <Box data-testid={ARTICLE_SITE_PAGE_TEST_ID} sx={{ p: 3, maxWidth: 960 }}>
      <Typography variant="h5" component="h1">
        Site &amp; domain
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Where readers find your published articles: your article domain, its home page, and an
        optional path on your main site.
      </Typography>
      <Divider sx={{ my: 3 }} />

      <Box component="section" data-testid={ARTICLE_SITE_DOMAIN_SECTION_TEST_ID}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <DnsIcon color="action" />
          <Box>
            <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
              Article domain
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The subdomain that serves your published articles.
            </Typography>
          </Box>
        </Stack>
        <CustomDomainSection projectId={projectId} surface="article" suppressGuideAnchors />
      </Box>
    </Box>
  );
}

export default ArticleSitePage;
